import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Tv } from "lucide-react";
import api from "../lib/api";
import { ASSETS, RECURRING_SERVICES, nextOccurrence } from "../lib/assets";
import RadioPlayer from "../components/RadioPlayer";
import EventCarousel from "../components/EventCarousel";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// Build "virtual" event objects from recurring services so the home carousel can include them.
function buildRecurringForCarousel() {
  return RECURRING_SERVICES.map((s) => {
    const next = nextOccurrence(s.weekday);
    next.setHours(s.startHour, 0, 0, 0);
    return {
      id: s.id,
      title: s.title,
      description: `${s.weekdayLabel} · ${s.timeLabel}. ${s.description}`,
      location: s.location,
      event_date: next.toISOString(),
      image_url: ASSETS.scrantonBg,
      featured: false,
      _recurring: true,
    };
  });
}

export default function Home() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  // Carousel = featured + a second slide using the alternate flyer + upcoming admin events + recurring services
  const featuredEvents = events.filter((e) => e.featured);
  const otherEvents = events.filter((e) => !e.featured);
  const altFlyerSlides = featuredEvents.map((e) => ({
    ...e,
    id: `${e.id}-alt`,
    image_url: ASSETS.flyerB,
  }));
  const carouselItems = [
    ...featuredEvents,
    ...altFlyerSlides,
    ...otherEvents,
    ...buildRecurringForCarousel(),
  ];

  return (
    <div data-testid="home-page" className="pt-16">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[85vh] flex items-center"
      >
        <div className="absolute inset-0">
          <img
            src={ASSETS.scrantonBg}
            alt="Scranton For Christ"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/65 to-[color:var(--inv-primary)]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 w-full">
          <div className="lg:col-span-8 text-white fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs tracking-widest uppercase mb-6">
              <Sparkles size={14} className="text-[color:var(--inv-primary)]" />
              <span>Iglesia Pentecostal · Scranton, PA</span>
            </div>
            <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight">
              Scranton <br className="hidden sm:block" />
              <span className="text-[color:var(--inv-primary)]">para Cristo.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-white/85 leading-relaxed">
              Bienvenido a Iglesia Nueva Vida Internacional. Una familia de fe
              que predica el evangelio con poder, adora con libertad y sirve a
              la comunidad de Scranton con amor.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/eventos"
                data-testid="hero-events-btn"
                className="btn-pill btn-pill-primary"
              >
                Próximo evento <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/tv"
                data-testid="hero-tv-btn"
                className="btn-pill bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/15"
              >
                <Tv size={16} /> Modo TV
              </Link>
              <Link
                to="/radio"
                data-testid="hero-radio-btn"
                className="btn-pill bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/15"
              >
                Escuchar la radio
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RADIO STRIP */}
      <section
        data-testid="radio-strip"
        className="max-w-7xl mx-auto px-6 lg:px-10 -mt-16 relative z-10"
      >
        <RadioPlayer />
      </section>

      {/* FEATURED EVENT CAROUSEL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--inv-text-muted)] mb-2">
              Próximamente
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              Eventos y cultos
            </h2>
          </div>
          <Link to="/tv" className="btn-pill btn-pill-secondary text-sm">
            <Tv size={16} /> Ver en modo TV
          </Link>
        </div>
        <EventCarousel events={carouselItems} />
      </section>

      {/* NEWS GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Últimas noticias
          </h2>
          <Link to="/noticias" className="btn-pill btn-pill-secondary text-sm">
            Todas las noticias
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {news.slice(0, 3).map((n) => (
            <article
              key={n.id}
              data-testid={`news-card-${n.id}`}
              className="bg-surface rounded-2xl border border-warm overflow-hidden hover-lift flex flex-col"
            >
              {n.image_url && (
                <div className="aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={n.image_url}
                    alt={n.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <div className="text-xs uppercase tracking-widest text-[color:var(--inv-text-muted)] mb-2">
                  {formatDate(n.published_at)}
                </div>
                <h4 className="font-heading text-xl font-semibold leading-snug tracking-tight">
                  {n.title}
                </h4>
                <p className="mt-2 text-sm text-[color:var(--inv-text-muted)] line-clamp-3">
                  {n.summary}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* VERSE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="bg-surface-alt rounded-2xl p-8 md:p-14 border border-warm">
          <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--inv-primary)] font-semibold mb-3">
            Palabra de la semana
          </div>
          <blockquote className="font-heading text-2xl md:text-4xl tracking-tight leading-[1.15] max-w-4xl">
            "Porque donde dos o tres se reúnen en mi nombre, allí estoy yo en
            medio de ellos."
          </blockquote>
          <div className="mt-4 text-sm text-[color:var(--inv-text-muted)]">
            Mateo 18:20
          </div>
        </div>
      </section>
    </div>
  );
}
